import { useCallback, useEffect, useMemo, useRef } from 'react'
import { trackEvent } from 'analytics/analytics'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import config from 'constants/config'
import type { ExperimentId } from 'constants/experiments'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getExperimentActivationEvent } from 'lib/optimizely/experimentActivation'
import {
  isAllowedToTriggerExperiment,
  setOptimizelyTriggeredCookie,
} from 'lib/optimizely/optimizelyUtils'

const activatableExperimentSet = new Set([
  ...(config.OPTIMIZELY_EXPERIMENTS ?? []),
])

function useExperimentActivation(experimentId: ExperimentId, canTrigger: boolean = true, overrideTrigger = false) {
  const dispatch = useAppDispatch()
  const hasTriggeredEvent = useRef(false)
  const previousVariationKey = useRef<string | undefined>(undefined)
  const utmContent = useAppSelector(state => state.utm.content)
  const isPostLoginOptimizelySyncing = useAppSelector(state => state.optimizely.isPostLoginOptimizelySyncing)

  const canTriggerExperiment = useMemo(() => {
    if (!activatableExperimentSet.has(experimentId)) {
      return false
    }
    if (isPostLoginOptimizelySyncing) {
      return false
    }
    if (overrideTrigger) {
      return canTrigger
    }
    return isAllowedToTriggerExperiment(canTrigger, utmContent, experimentId)
  }, [canTrigger, isPostLoginOptimizelySyncing, overrideTrigger, utmContent, experimentId])

  const experiment = useAppSelector(state => state.optimizely.optimizelyExperiments[experimentId])

  const activate = useCallback(() => {
    const isActivatableExperiment = activatableExperimentSet.has(experimentId) && (
      overrideTrigger || isAllowedToTriggerExperiment(true, utmContent, experimentId)
    )
    if (isPostLoginOptimizelySyncing || !isActivatableExperiment || hasTriggeredEvent.current || experiment?.triggered || !experiment) {
      return
    }

    const activationEvent = getExperimentActivationEvent(experimentId, experiment)
    if (activationEvent) {
      trackEvent(activationEvent)

      dispatch(setOptimizelyExperiment(experimentId, experiment))
      setOptimizelyTriggeredCookie(experimentId)
      hasTriggeredEvent.current = true
    }
  }, [dispatch, experiment, experimentId, isPostLoginOptimizelySyncing, overrideTrigger, utmContent])

  const currentVariationKey = experiment?.variationKey
  const experimentTriggered = experiment?.triggered
  useEffect(() => {
    if (currentVariationKey !== previousVariationKey.current) {
      if (hasTriggeredEvent.current && experimentTriggered === false) {
        hasTriggeredEvent.current = false
      }
      previousVariationKey.current = currentVariationKey
    }
  }, [currentVariationKey, experimentTriggered])

  useEffect(() => {
    if (canTriggerExperiment) {
      activate()
    }
  }, [activate, canTriggerExperiment])

  return activate
}

export default useExperimentActivation
